import React, { useState } from 'react';
import { Users, LayoutTemplate } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TemplatesList } from '@/components/schedules/templates/TemplatesList';
import { ScheduleCalendar } from '@/components/schedules/calendar/ScheduleCalendar';
import { BulkEditModal } from '@/components/schedules/calendar/BulkEditModal';
import { TemplateFormDialog } from '@/components/schedules/templates/CreateTemplateDialog';
import { DeleteTemplateDialog } from '@/components/schedules/templates/DeleteTemplateDialog';
import { AssignEmployeesDialog } from '@/components/schedules/templates/AssignEmployeesDialog';
import { EditWorkTimeDialog } from '@/components/schedules/templates/EditWorkTimeDialog';
import { DepartmentScheduleView, type CreateExceptionData } from '@/components/schedules/department/DepartmentScheduleView';
import { CreateExceptionDialog } from '@/components/schedules/department/CreateExceptionDialog';
import { ExceptionDetailsDialog } from '@/components/schedules/department/ExceptionDetailsDialog';
import { useUpdateCalendarDays } from '@/hooks/schedules/useTemplateCalendar';
import type { ScheduleTemplate, CalendarDay } from '@/types/schedule.types';

export const Schedules: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('schedules', 'write');
  const [activeTab, setActiveTab] = useState<'templates' | 'departments'>('templates');
  const [selectedTemplate, setSelectedTemplate] = useState<ScheduleTemplate | null>(null);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedCalendarDays, setSelectedCalendarDays] = useState<CalendarDay[]>([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isEditWorkTimeDialogOpen, setIsEditWorkTimeDialogOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<ScheduleTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<ScheduleTemplate | null>(null);
  const [templateToAssign, setTemplateToAssign] = useState<ScheduleTemplate | null>(null);
  const [templateToEditWorkTime, setTemplateToEditWorkTime] = useState<ScheduleTemplate | null>(null);
  const [currentYear] = useState(new Date().getFullYear());
  const [currentMonth] = useState(new Date().getMonth() + 1);

  // Exception dialogs state
  const [exceptionData, setExceptionData] = useState<CreateExceptionData | null>(null);
  const [viewExceptionId, setViewExceptionId] = useState<string | null>(null);

  // Use calendar hook for bulk updates
  const updateCalendarDays = useUpdateCalendarDays(selectedTemplate?.id || null);

  const handleSelectTemplate = (template: ScheduleTemplate) => {
    setSelectedTemplate(template);
    setSelectedDays([]); // Clear selection when switching templates
    setSelectedCalendarDays([]);
  };

  const handleDayClick = (day: CalendarDay) => {
    console.log('Day clicked:', day);
  };

  const handleDaySelect = (dayIds: string[]) => {
    setSelectedDays(dayIds);
  };

  const handleBulkEdit = () => {
    setIsBulkEditOpen(true);
  };

  const handleBulkUpdate = async (dayIds: string[], updates: Partial<CalendarDay>) => {
    if (selectedTemplate) {
      await updateCalendarDays.mutateAsync({
        dayIds,
        updates
      });
      setSelectedDays([]); // Clear selection after update
    }
  };

  const handleCreateTemplate = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEditTemplate = (template: ScheduleTemplate) => {
    setTemplateToEdit(template);
    setIsEditDialogOpen(true);
  };

  const handleDeleteTemplate = (template: ScheduleTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const handleAssignTemplate = (template: ScheduleTemplate) => {
    setTemplateToAssign(template);
    setIsAssignDialogOpen(true);
  };

  const handleEditWorkTime = (template: ScheduleTemplate) => {
    setTemplateToEditWorkTime(template);
    setIsEditWorkTimeDialogOpen(true);
  };

  const handleAssignSuccess = () => {
    // Success callback - dialog will close automatically
    // Query invalidation will refresh the data
  };

  const handleTemplateCreated = () => {
    // Success callback - dialog will close automatically
    // Templates list will auto-refresh via query invalidation
  };

  const handleTemplateUpdated = () => {
    // Success callback - dialog will close automatically
    // Query invalidation will update the list
  };

  // Handlers for exception dialogs
  const handleCreateException = (data: CreateExceptionData) => {
    setExceptionData(data);
  };

  const handleViewException = (exceptionId: string) => {
    setViewExceptionId(exceptionId);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'templates' | 'departments')}
        className="h-full flex flex-col"
      >
        <div className="border-b px-4 py-2">
          <TabsList>
            <TabsTrigger value="templates" className="gap-2">
              <LayoutTemplate className="h-4 w-4" />
              Шаблоны графиков
            </TabsTrigger>
            <TabsTrigger value="departments" className="gap-2">
              <Users className="h-4 w-4" />
              По отделам
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="templates" className="flex-1 mt-0 overflow-hidden">
          <div className="flex h-full">
            <div className="w-80 flex-shrink-0 h-full overflow-hidden">
              <TemplatesList
                selectedTemplateId={selectedTemplate?.id || null}
                onSelectTemplate={handleSelectTemplate}
                onCreateTemplate={canWrite ? handleCreateTemplate : undefined}
                onEditTemplate={canWrite ? handleEditTemplate : undefined}
                onEditWorkTime={canWrite ? handleEditWorkTime : undefined}
                onDeleteTemplate={canWrite ? handleDeleteTemplate : undefined}
                onAssignTemplate={canWrite ? handleAssignTemplate : undefined}
              />
            </div>

            <div className="flex-1 overflow-hidden">
              <ScheduleCalendar
                selectedTemplate={selectedTemplate}
                onDayClick={handleDayClick}
                selectedDays={selectedDays}
                onDaySelect={handleDaySelect}
                onSelectedDaysChange={setSelectedCalendarDays}
                onBulkEdit={handleBulkEdit}
                onBulkUpdate={handleBulkUpdate}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="departments" className="flex-1 mt-0 p-4 min-h-0 flex flex-col overflow-y-auto">
          <DepartmentScheduleView
            onCreateException={canWrite ? handleCreateException : undefined}
            onViewException={handleViewException}
          />
        </TabsContent>
      </Tabs>

      {selectedTemplate && (
        <BulkEditModal
          isOpen={isBulkEditOpen}
          onClose={() => setIsBulkEditOpen(false)}
          selectedDays={selectedCalendarDays}
          onUpdate={handleBulkUpdate}
          templateName={selectedTemplate.name}
        />
      )}

      <TemplateFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleTemplateCreated}
        mode="create"
      />

      <TemplateFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleTemplateUpdated}
        mode="edit"
        template={templateToEdit}
      />

      <DeleteTemplateDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        template={templateToDelete}
      />

      <AssignEmployeesDialog
        open={isAssignDialogOpen}
        onOpenChange={setIsAssignDialogOpen}
        template={templateToAssign}
        onSuccess={handleAssignSuccess}
      />

      {/* Exception Dialogs */}
      <CreateExceptionDialog
        open={!!exceptionData}
        onOpenChange={(open) => {
          if (!open) {
            setExceptionData(null);
          }
        }}
        employeeId={exceptionData?.employeeId ?? null}
        date={exceptionData?.date ?? null}
        employeeName={exceptionData?.employeeName}
        employeesWithSchedule={exceptionData?.employeesWithSchedule}
      />

      <ExceptionDetailsDialog
        open={!!viewExceptionId}
        onOpenChange={(open) => {
          if (!open) {
            setViewExceptionId(null);
          }
        }}
        exceptionId={viewExceptionId}
      />

      <EditWorkTimeDialog
        open={isEditWorkTimeDialogOpen}
        onOpenChange={setIsEditWorkTimeDialogOpen}
        template={templateToEditWorkTime}
      />
    </div>
  );
};